import * as React from 'react'; // https://github.com/aelbore/esbuild-jest/issues/61
import wrapNonBlocksInContainer from '../helpers/wrapNonBlocksInContainer';
import isLineBreak from '../helpers/isLineBreak';
import Serializer, {
  type SerializeArguments,
  type DeserializeArguments,
} from './Serializer';
import { Editor, Element, Text } from 'slate';
import { type CSSProperties } from 'react';
import AlignmentUtils from '../../plugins/AlignmentPlugin/AlignmentUtils';
import BlockUtils from '../../plugins/Blocks/BlockUtils';
import { isHTMLElementNode } from '../helpers/serializerHelpers';
import { isCommonBlockElement } from '../../types/guards';
import LineHeightUtils from '../../plugins/LineHeightPlugin/LineHeightUtils';
import addEmptyTextAtInlineEdges from '../helpers/addEmptyTextAtInlineEdges';

export const BlockElements = {
  p: 'paragraph',
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  blockquote: 'quote',
};

class SerializerBlock extends Serializer {
  static tagNames = Object.keys(BlockElements);
  static nodeNames = Object.values(BlockElements);

  static serialize({ node, children }: SerializeArguments) {
    if (!isCommonBlockElement(node)) return;

    const Tag = this.tagNames[
      this.nodeNames.indexOf(node.type)
    ] as keyof JSX.IntrinsicElements;

    const style: CSSProperties = AlignmentUtils.alignmentStyleForElement(node);

    // @ts-expect-error TODO: Are we using this?
    const anchor = node.link_anchor;

    return (
      <Tag
        style={style}
        data-anchor={anchor}
        id={BlockUtils.blockAnchorId(node)}
      >
        {children}
        {isLineBreak(node) && <br />}
      </Tag>
    );
  }

  static deserialize({
    el,
    markAttributes,
    next,
    context,
  }: DeserializeArguments) {
    if (!isHTMLElementNode(el)) return;
    if (!context.editor) return;

    const tagName = el.tagName.toLowerCase();
    if (!this.tagNames.includes(tagName)) return;

    let children = next(el.childNodes, markAttributes);

    const data = {} as any;

    const alignment = AlignmentUtils.alignmentDataFromHtmlElement(el);
    if (alignment) {
      data.alignment = alignment;
    }

    const lineHeight = LineHeightUtils.lineHeightDataFromHtmlElement(el);
    if (lineHeight) {
      data.lineHeight = lineHeight;
    }

    const anchor = el.getAttribute('data-anchor');
    if (anchor && anchor !== '') {
      data.link_anchor = anchor;
    }

    // If our child nodes are a mix of blocks and text nodes, the
    // default Slate normalizers will strip out any block children
    // of this node. We don't want that, because it will cause us to
    // lose content. So, if we have a block child, we should wrap
    // runs of non-block children in blocks.
    const hasTextNodes = children.some(
      (node) =>
        context.editor &&
        ((Element.isElement(node) && Editor.isInline(context.editor, node)) ||
          Text.isText(node))
    );
    const hasBlockNodes = children.some(
      (node) =>
        context.editor &&
        Element.isElement(node) &&
        Editor.isBlock(context.editor, node) &&
        !Text.isText(node)
    );

    if (hasTextNodes && hasBlockNodes) {
      children = wrapNonBlocksInContainer(
        context.editor,
        children,
        'paragraph'
      );
    } else if (hasTextNodes) {
      children = addEmptyTextAtInlineEdges(context.editor, children);
    }

    if (children.length === 0) {
      children.push({
        text: '',
      });
    }

    return {
      type: this.nodeNames[this.tagNames.indexOf(tagName)],
      children,
      ...data,
    };
  }
}

export default SerializerBlock;
